import { supabase } from "./supabase";

const TENANT_DOMAIN = "ikone.rs";
const TENANT_ID = "bc87b153-b717-4234-bb79-d3d6d131ef1d";

async function getTenantId() {
  const { data } = await supabase.from("tenants").select("id").eq("domain", TENANT_DOMAIN).single();
  return data?.id || TENANT_ID;
}

export async function getZones() {
  const tenantId = await getTenantId();
  const { data, error } = await supabase
    .from("delivery_zones")
    .select("*")
    .eq("tenant_id", tenantId)
    .order("name");
  if (error) throw error;
  return data || [];
}

// Returns available hours [8,9,...,17] for a zone on a given date
// Logic: same zone = no cross-block; different zones must be ≥4h apart
// Sundays are blocked unless manually unblocked via delivery_unblocks
export async function getAvailableHours(zoneId, date) {
  // Nedelja — odmah vrati [] osim ako ima unblock entryja
  const dow = new Date(date + "T12:00:00").getDay();
  if (dow === 0) {
    const { data: unblocks } = await supabase
      .from("delivery_unblocks")
      .select("unblock_hour")
      .eq("tenant_id", TENANT_ID)
      .eq("unblock_date", date);
    if (!unblocks || unblocks.length === 0) return [];
    // Postoji bar jedan unblock — nastavi normalnom logikom ali blokiraj sate bez unbloka
    const unblockedHours = new Set(
      unblocks.filter((u) => u.unblock_hour !== null).map((u) => u.unblock_hour)
    );
    const fullDayUnblocked = unblocks.some((u) => u.unblock_hour === null);
    if (!fullDayUnblocked) {
      // Samo određeni sati su odblokirani — delivery_blocks se ignoriše za nedelje
      const [{ data: zone }, { data: bookings }] = await Promise.all([
        supabase.from("delivery_zones").select("max_per_hour").eq("id", zoneId).single(),
        supabase.from("orders").select("delivery_hour, delivery_zone_id")
          .eq("tenant_id", TENANT_ID).eq("delivery_date", date).not("delivery_hour", "is", null),
      ]);
      const maxPerHour = zone?.max_per_hour ?? 1;
      const existing = bookings || [];
      const available = [];
      for (let h = 8; h <= 17; h++) {
        if (!unblockedHours.has(h)) continue;
        const sameZoneCount = existing.filter((b) => b.delivery_zone_id === zoneId && b.delivery_hour === h).length;
        if (sameZoneCount >= maxPerHour) continue;
        const crossZoneConflict = existing.some((b) => b.delivery_zone_id !== zoneId && Math.abs(b.delivery_hour - h) < 4);
        if (crossZoneConflict) continue;
        available.push(h);
      }
      return available;
    }
    // fullDayUnblocked = true — nastavi normalno ispod
  }

  const [{ data: zone }, { data: bookings }, { data: blocks }] = await Promise.all([
    supabase.from("delivery_zones").select("max_per_hour").eq("id", zoneId).single(),
    supabase.from("orders").select("delivery_hour, delivery_zone_id")
      .eq("tenant_id", TENANT_ID).eq("delivery_date", date).not("delivery_hour", "is", null),
    supabase.from("delivery_blocks").select("zone_id, block_hour")
      .eq("tenant_id", TENANT_ID).eq("block_date", date)
      .or(`zone_id.eq.${zoneId},zone_id.is.null`),
  ]);

  const maxPerHour = zone?.max_per_hour ?? 1;
  const existing = bookings || [];
  const blockList = blocks || [];

  // Full-day block for this zone or all zones
  const fullDayBlocked = blockList.some((b) => b.block_hour === null);
  if (fullDayBlocked) return [];

  const available = [];

  for (let h = 8; h <= 17; h++) {
    // Admin block for this specific hour
    if (blockList.some((b) => b.block_hour === h)) continue;

    // Same-zone capacity
    const sameZoneCount = existing.filter(
      (b) => b.delivery_zone_id === zoneId && b.delivery_hour === h
    ).length;
    if (sameZoneCount >= maxPerHour) continue;

    // Cross-zone 4h gap
    const crossZoneConflict = existing.some(
      (b) => b.delivery_zone_id !== zoneId && Math.abs(b.delivery_hour - h) < 4
    );
    if (crossZoneConflict) continue;

    available.push(h);
  }

  return available;
}

export async function reserveSlot(slotId) {
  const { error } = await supabase.rpc("reserve_delivery_slot", { slot_id: slotId });
  if (error) throw error;
}
